import React, { useState } from 'react';
import { X, Search, Plus } from 'lucide-react';

const DaycareChildrenModal = ({ isOpen, onClose, onSuccess, families, apiCall, showError }) => {
  const [step, setStep] = useState(1); // 1: Select/Create Family, 2: Child Details
  const [familySearch, setFamilySearch] = useState('');
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [creatingNewFamily, setCreatingNewFamily] = useState(false);
  
  const [newFamilyData, setNewFamilyData] = useState({
    familyId: '',
    fatherName: '',
    motherName: '',
    address: '',
    primaryPhone: '',
    secondaryPhone: '',
    email: '',
    notes: ''
  });
  const [loadingFamilyId, setLoadingFamilyId] = useState(false);

  // Fetch next available Family ID from database
  const fetchNextFamilyId = async () => {
    setLoadingFamilyId(true);
    try {
      // Get the last family ID from the database
      const families = await apiCall('/families');
      
      if (!families || families.length === 0) {
        // If no families exist, start with 7369-00001
        return '7369-00001';
      }

      // Find the highest family ID
      let maxNumber = 0;
      families.forEach(family => {
        const match = family.FamilyID.match(/^\d+-(\d+)$/);
        if (match) {
          const num = parseInt(match[1], 10);
          if (num > maxNumber) {
            maxNumber = num;
          }
        }
      });

      // Increment and format
      const nextNumber = (maxNumber + 1).toString().padStart(5, '0');
      return `7369-${nextNumber}`;
    } catch (error) {
      showError('Error fetching next Family ID: ' + error.message);
      // Fallback to timestamp-based ID
      const timestamp = Date.now().toString().slice(-5);
      return `7369-${timestamp}`;
    } finally {
      setLoadingFamilyId(false);
    }
  };

  // Generate Family ID when opening create family form
  const handleCreateFamilyClick = async () => {
    setCreatingNewFamily(true);
    const nextId = await fetchNextFamilyId();
    setNewFamilyData({
      ...newFamilyData,
      familyId: nextId
    });
  };

  const [formData, setFormData] = useState({
    familyId: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: 'Male',
    medicalNotes: '',
    allergies: '',
    emergencyContact: '',
    emergencyPhone: '',
    studentId: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Filter families based on search
  const filteredFamilies = families.filter(family => {
    const searchLower = familySearch.toLowerCase();
    return (
      family.FamilyID.toLowerCase().includes(searchLower) ||
      family.FatherName?.toLowerCase().includes(searchLower) ||
      family.MotherName?.toLowerCase().includes(searchLower) ||
      family.PhoneNumber?.includes(searchLower)
    );
  });

  const handleFamilySelect = (family) => {
    setSelectedFamily(family);
    setFormData({ ...formData, familyId: family.FamilyID });
    setStep(2);
  };

  const handleCreateNewFamily = async () => {
    if (!newFamilyData.familyId) {
      showError('Family ID is required');
      return;
    }
    
    if (!newFamilyData.fatherName && !newFamilyData.motherName) {
      showError('Please enter at least Father Name or Mother Name');
      return;
    }

    if (!newFamilyData.primaryPhone) {
      showError('Primary phone is required');
      return;
    }

    setSubmitting(true);
    try {
      // Create new family
      await apiCall('/families', {
        method: 'POST',
        body: JSON.stringify(newFamilyData)
      });
      
      // Use the newly created family
      setSelectedFamily({
        FamilyID: newFamilyData.familyId,
        FatherName: newFamilyData.fatherName,
        MotherName: newFamilyData.motherName
      });
      setFormData({ ...formData, familyId: newFamilyData.familyId });
      setCreatingNewFamily(false);
      setStep(2);
    } catch (error) {
      showError('Error creating family: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.familyId || !formData.firstName || !formData.lastName || !formData.dateOfBirth) {
      showError('Please fill in all required fields');
      return;
    }

    console.log('🔍 MODAL DEBUG: Submitting child data:', formData);

    setSubmitting(true);
    try {
      console.log('🔍 MODAL DEBUG: Calling API...');
      const result = await apiCall('/daycare/children', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      console.log('🔍 MODAL DEBUG: API result:', result);
      
      if (!result) {
        console.error('❌ MODAL DEBUG: API returned null/undefined');
        showError('Failed to register child - no response from server');
        return;
      }
      
      if (result.error) {
        console.error('❌ MODAL DEBUG: API returned error:', result.error);
        showError('Error: ' + result.error);
        return;
      }
      
      console.log('✅ MODAL DEBUG: Success! Calling onSuccess()');
      onSuccess();
    } catch (error) {
      console.error('❌ MODAL DEBUG: Exception caught:', error);
      showError('Error registering child: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetModal = () => {
    setStep(1);
    setFamilySearch('');
    setSelectedFamily(null);
    setCreatingNewFamily(false);
    setNewFamilyData({
      familyId: '',
      fatherName: '',
      motherName: '',
      address: '',
      primaryPhone: '',
      secondaryPhone: '',
      email: '',
      notes: ''
    });
    setFormData({
      familyId: '',
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: 'Male',
      medicalNotes: '',
      allergies: '',
      emergencyContact: '',
      emergencyPhone: '',
      studentId: ''
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => { resetModal(); onClose(); }}></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                {step === 1 ? 'Step 1: Select or Create Family' : 'Step 2: Child Information'}
              </h3>
              <button
                onClick={() => { resetModal(); onClose(); }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {/* STEP 1: SELECT OR CREATE FAMILY */}
            {step === 1 && !creatingNewFamily && (
              <div>
                {/* Search Bar */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Family
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search by Family ID, Father Name, Mother Name, or Phone..."
                      value={familySearch}
                      onChange={(e) => setFamilySearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Create New Family Button */}
                <div className="mb-4">
                  <button
                    type="button"
                    onClick={handleCreateFamilyClick}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg text-blue-600 hover:bg-blue-50 hover:border-blue-400 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    <span className="font-medium">Family Not in System? Create New Family</span>
                  </button>
                </div>

                {/* Family List */}
                <div className="max-h-96 overflow-y-auto border rounded-lg">
                  {filteredFamilies.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      No families found. Try a different search or create a new family.
                    </div>
                  ) : (
                    <div className="divide-y">
                      {filteredFamilies.map((family) => (
                        <div
                          key={family.FamilyID}
                          onClick={() => handleFamilySelect(family)}
                          className="p-4 hover:bg-blue-50 cursor-pointer transition-colors"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold text-gray-900">
                                {family.FamilyID}
                              </p>
                              <p className="text-sm text-gray-600 mt-1">
                                Father: {family.FatherName || 'N/A'}
                              </p>
                              <p className="text-sm text-gray-600">
                                Mother: {family.MotherName || 'N/A'}
                              </p>
                              {family.PhoneNumber && (
                                <p className="text-xs text-gray-500 mt-1">
                                  📞 {family.PhoneNumber}
                                </p>
                              )}
                            </div>
                            <button className="text-blue-600 text-sm font-medium hover:text-blue-800">
                              Select →
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* STEP 1B: CREATE NEW FAMILY */}
            {step === 1 && creatingNewFamily && (
              <div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    Create a new family record for children not currently enrolled in the school system.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Family ID - Auto-generated but editable */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Family ID <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newFamilyData.familyId}
                        onChange={(e) => setNewFamilyData({ ...newFamilyData, familyId: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                      <button
                        type="button"
                        onClick={async () => {
                          const nextId = await fetchNextFamilyId();
                          setNewFamilyData({ ...newFamilyData, familyId: nextId });
                        }}
                        disabled={loadingFamilyId}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:bg-gray-50"
                      >
                        {loadingFamilyId ? '...' : '🔄 Next ID'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Format: 7369-XXXXX (auto-incremented from last family)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Father Name
                    </label>
                    <input
                      type="text"
                      value={newFamilyData.fatherName}
                      onChange={(e) => setNewFamilyData({ ...newFamilyData, fatherName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mother Name
                    </label>
                    <input
                      type="text"
                      value={newFamilyData.motherName}
                      onChange={(e) => setNewFamilyData({ ...newFamilyData, motherName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <input
                      type="text"
                      value={newFamilyData.address}
                      onChange={(e) => setNewFamilyData({ ...newFamilyData, address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Primary Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={newFamilyData.primaryPhone}
                      onChange={(e) => setNewFamilyData({ ...newFamilyData, primaryPhone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Secondary Phone
                    </label>
                    <input
                      type="tel"
                      value={newFamilyData.secondaryPhone}
                      onChange={(e) => setNewFamilyData({ ...newFamilyData, secondaryPhone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={newFamilyData.email}
                      onChange={(e) => setNewFamilyData({ ...newFamilyData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={newFamilyData.notes}
                      onChange={(e) => setNewFamilyData({ ...newFamilyData, notes: e.target.value })}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="mt-4 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setCreatingNewFamily(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateNewFamily}
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
                  >
                    {submitting ? 'Creating...' : 'Create Family & Continue'}
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: CHILD INFORMATION */}
            {step === 2 && (
              <form onSubmit={handleSubmit}>
                {/* Selected Family Info */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="font-semibold text-green-900">
                    Family: {selectedFamily.FamilyID}
                  </p>
                  <p className="text-sm text-green-700">
                    {selectedFamily.FatherName} / {selectedFamily.MotherName}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* First Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Date of Birth */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Gender */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gender
                    </label>
                    <select
                      value={formData.gender}
                      onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>

                  {/* Medical Notes */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Medical Notes
                    </label>
                    <textarea
                      value={formData.medicalNotes}
                      onChange={(e) => setFormData({ ...formData, medicalNotes: e.target.value })}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Allergies */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Allergies
                    </label>
                    <textarea
                      value={formData.allergies}
                      onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Emergency Contact */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Emergency Contact Name
                    </label>
                    <input
                      type="text"
                      value={formData.emergencyContact}
                      onChange={(e) => setFormData({ ...formData, emergencyContact: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Emergency Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Emergency Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.emergencyPhone}
                      onChange={(e) => setFormData({ ...formData, emergencyPhone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-between border-t border-gray-200">
            <div>
              {step === 2 && (
                <button
                  type="button"
                  onClick={() => { setStep(1); setSelectedFamily(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  ← Back to Family Selection
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { resetModal(); onClose(); }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              {step === 2 && (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
                >
                  {submitting ? 'Registering...' : 'Register Child'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DaycareChildrenModal;